import hexToRgba from './hexToRgba';

// Validated rather than trusted: the value crosses the Delfino bridge from the host.
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

const WHITE = 255;
const BLACK = 0;

// Brand colors are picked against white, so dark lightens them by the same amount that
// turns light's #2300ff into #bbb0ff, the accent that theme ships.
const ACCENT_WEIGHT_IN_DARK = 0.31;

const ACCENT_BG_ALPHA_IN_LIGHT = 0.1;
const ACCENT_BG_ALPHA_IN_DARK = 0.15;

// The hairline ring the add-on marker draws around its expanded chip.
const ACCENT_RING_ALPHA = 0.45;

// The progress track is a tint of the fill, and both themes shipped it as one: #e9e6ff is
// light's accent at 10% on white, #4a4166 is dark's at 39% on black. Kept as weights so a
// tenant accent carries its own track instead of the built-in violet.
const PROGRESS_TRACK_WEIGHT_IN_LIGHT = 0.1;
const PROGRESS_TRACK_WEIGHT_IN_DARK = 0.4;

/**
 * Blends a hex color with white or black, keeping `weight` of it in each channel.
 * Kept out of CSS on purpose: color-mix() is unparsable on the oldest engines Fenice ships
 * to, and getComputedStyle would hand the plugin iframe its raw token instead of a color.
 * @param {string} hex - 6-digit hex color
 * @param {number} weight - share kept from the original color, between 0 and 1
 * @param {number} target - channel value blended in, WHITE or BLACK
 * @returns {string} 6-digit hex color
 */
const mixToward = (hex, weight, target) => {
    const mixed = [1, 3, 5]
        .map(index => parseInt(hex.slice(index, index + 2), 16))
        .map(channel => Math.round(channel * weight + target * (1 - weight)))
        .map(channel => channel.toString(16).padStart(2, '0'));

    return `#${mixed.join('')}`;
};

/**
 * Applies a tenant's brand accent to the document root, with the variants the light and
 * dark themes read. Absent or malformed values leave every property unset, which is what
 * falls back to each theme's own accent.
 * @param {string} [accentColor] - 6-digit hex color, e.g. '#0066CC'
 * @returns {void}
 */
const setTenantAccentColor = accentColor => {
    if (!accentColor || !HEX_COLOR.test(accentColor)) return;

    const inDark = mixToward(accentColor, ACCENT_WEIGHT_IN_DARK, WHITE);
    const { style } = document.documentElement;

    style.setProperty('--tenant-accent-color', accentColor);
    style.setProperty('--tenant-accent-bg', hexToRgba(accentColor, ACCENT_BG_ALPHA_IN_LIGHT));
    style.setProperty('--tenant-accent-ring', hexToRgba(accentColor, ACCENT_RING_ALPHA));
    style.setProperty('--tenant-accent-track', mixToward(accentColor, PROGRESS_TRACK_WEIGHT_IN_LIGHT, WHITE));
    style.setProperty('--tenant-accent-dark-color', inDark);
    style.setProperty('--tenant-accent-dark-bg', hexToRgba(inDark, ACCENT_BG_ALPHA_IN_DARK));
    style.setProperty('--tenant-accent-dark-ring', hexToRgba(inDark, ACCENT_RING_ALPHA));
    style.setProperty('--tenant-accent-dark-track', mixToward(inDark, PROGRESS_TRACK_WEIGHT_IN_DARK, BLACK));
};

export default setTenantAccentColor;
